"use client";
import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";

import "../../marken/marken.css";

import { TopSectionMarken } from "../../marken/sections/TopSection";
import { AutoVerkaufenGrid } from "@/common/AutoVerkaufenGrid";
import { Banner } from "@/common/Banner";
import sellCarCash from "@/assets/auto-verkaufen/Rückbank-autoverkauf.avif";

import höchstenPreis from "@/assets/auto-verkaufen/Frau-verkauft-ihr-auto.webp";
import BildTextInfo from "@/components/BildTextInfo/BildTextInfo";

import { HäufigeFragen } from "@/common/HäufigeFragen";
import FAQImage from "@/assets/faq-autoverkauf-autoeinfachlos.webp";
import { fragenAutoMitFinanzierungVerkaufen } from "./data";

// Ratgeber SERVERSEITIG rendern (dynamic OHNE die SSR-Abschaltung, Playbook 2.6):
// die sechs "Mehr zum Thema"-Kacheln verlinken u. a. /auto-verkaufen.
const Ratgeber = dynamic(() =>
  import("@/components/Ratgeber/Ratgeber").then((m) => m.default)
);

// Only needed when the user reaches step 12; avoid loading auth-related JS/CSS on initial paint.
const AnmeldenSection = dynamic(() => import("@/app/anmelden/AnmeldenStepRedirect"), {
  ssr: false,
});

function AutoMitFinanzierungVerkaufen() {
  const [step, setStep] = useState(1);
  // selected values
  const [selectedBrand, setSelectedBrand] = useState({ id: "", name: "" });
  const [selectedModel, setSelectedModel] = useState({ id: "", name: "" });
  const [selectedYear, setSelectedYear] = useState({ id: "", name: "" });
  const [selectedBauform, setSelectedBauform] = useState({ id: "", name: "" });
  const [selectedFuel, setSelectedFuel] = useState({ id: "", name: "" });
  const [selectedVariant, setSelectedVariant] = useState({ id: "", name: "" });
  const [selectedPs, setSelectedPs] = useState({ id: "", name: "" });
  const [selectedTransmission, setSelectedTransmission] = useState({
    id: "",
    name: "",
  });
  const [selectedDoors, setSelectedDoors] = useState({ id: "", name: "" });
  const [selectedModelType, setSelectedModelType] = useState({
    id: "",
    name: "",
  });
  const [selectedKmStand, setSelectedKmStand] = useState("");
  const isSecondaryMethod = true;

  useEffect(() => {
    if (step >= 10) {
      if (typeof AnmeldenSection?.preload === "function") {
        AnmeldenSection.preload();
      } else {
        import("@/app/anmelden/AnmeldenStepRedirect");
      }
    }
  }, [step]);


  return (
    <div className="home-container">
      <TopSectionMarken
        title="Auto mit Finanzierung verkaufen – so geht’s"
        link="/guides/auto-mit-finanzierung-verkaufen"
        linkname="Auto mit Finanzierung verkaufen"
        text={
          <>
            84% der Autoeinfachlos-Kunden bekommen mehr Geld.*{" "}
            <br className="desktop" />
            Hol dir deine kostenlose und sofortige Bewertung.
          </>
        }
        removePadding={true}
        step={step}
        setStep={setStep}
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedBauform={selectedBauform}
        setSelectedBauform={setSelectedBauform}
        selectedFuel={selectedFuel}
        setSelectedFuel={setSelectedFuel}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        selectedPs={selectedPs}
        setSelectedPs={setSelectedPs}
        selectedTransmission={selectedTransmission}
        setSelectedTransmission={setSelectedTransmission}
        selectedDoors={selectedDoors}
        setSelectedDoors={setSelectedDoors}
        setSelectedModelType={setSelectedModelType}
        selectedModelType={selectedModelType}
        selectedKmStand={selectedKmStand}
        setSelectedKmStand={setSelectedKmStand}
      />

      {step === 12 && (
        <>
          <AnmeldenSection
            title="Bitte gib deine E-Mail-Adresse ein, um fortzufahren"
            fin={null}
            isManual={isSecondaryMethod}
            marke={selectedBrand}
            model={selectedModel}
            baujahr={selectedYear}
            bodytype={selectedBauform}
            fueltype={selectedFuel}
            variant={selectedVariant}
            ps={selectedPs}
            gearbox={selectedTransmission}
            doors={selectedDoors}
            modelType={selectedModelType}
            kmStand={selectedKmStand}
            page="/auto-verkaufen"
          />
          <Banner removeContent className="Banner-anmelden" />
        </>
      )}

      {step !== 12 && (
        <div className="Page background-color">
          <div className="Page-wrapper">
            <AutoVerkaufenGrid
              image={sellCarCash}
              leftTitle="Warum ist der Verkauf eines finanzierten Autos komplizierter?"
              leftText={
                <>
                  <p>
                    Ein Auto zu verkaufen, das noch finanziert ist, ist für
                    viele Besitzer eine Herausforderung. Rechtlich gehört der
                    Wagen bis zur vollständigen Zahlung der Bank. Die Bank
                    behält die Zulassungsbescheinigung Teil II (Fahrzeugbrief)
                    als Sicherheit und stimmt einem Verkauf erst zu, wenn die
                    Restschuld beglichen ist. Wir erklären dir, wie du vorgehst
                    und welche Optionen du hast.
                  </p>
                  <p>
                    Bei einer Finanzierungsform behält die Bank bis zur Tilgung
                    das Eigentum am Fahrzeug. Möchtest du verkaufen, musst du
                    die Bank kontaktieren, die Ablösesumme erfragen und das
                    Darlehen vorzeitig ablösen oder vom Käufer übernehmen
                    lassen.
                  </p>
                </>
              }
              rightFirstTitle="Deine Optionen beim Verkauf"
              rightFirstText={
                <>
                  <ul className="custom-unordered-list">
                    <li>
                      <strong>Kredit ablösen:</strong> Tilge die Restschuld
                      komplett. Sobald der Kredit beglichen ist, gibt die Bank
                      die Papiere frei und du kannst das Auto übertragen. Achte
                      auf mögliche Vorfälligkeitsentschädigungen.
                    </li>
                    <li>
                      <strong>Kredit übertragen:</strong> Einige Banken
                      erlauben, dass der Käufer den laufenden Kredit übernimmt,
                      sofern er kreditwürdig ist. Es wird ein neuer Vertrag
                      abgeschlossen, und der Käufer führt die Raten weiter.
                    </li>
                    <li>
                      <strong>Verkauf an Händler oder Autoeinfachlos:</strong>{" "}
                      Händler können die Restschuld direkt mit der Bank
                      verrechnen und zahlen dir den Restbetrag aus. Über
                      Autoeinfachlos erhältst du Angebote, bei denen die Händler
                      die Finanzierung ablösen.
                    </li>
                  </ul>

                  <h3>So verkaufst du dein Auto mit Finanzierung</h3>

                  <ul className="custom-unordered-list">
                    <li>
                      <strong>Restschuld ermitteln:</strong> Kontaktiere deine
                      Bank und lass dir die aktuelle Restkreditsumme und die
                      Ablösebedingungen schriftlich bestätigen.
                    </li>
                    <li>
                      <strong>Auto bewerten lassen:</strong> Nutze unseren
                      kostenlosen Rechner, um den Marktwert deines Autos zu
                      ermitteln.
                    </li>
                    <li>
                      <strong>Optionen vergleichen:</strong> Prüfe, ob du den
                      Kredit ablösen kannst oder ob der Käufer ihn übernehmen
                      sollte. Hol dir Angebote ein.
                    </li>
                    <li>
                      <strong>Kaufvertrag und Bankabwicklung:</strong> Bei
                      Kreditübernahme muss die Bank zustimmen. Beim Verkauf an
                      einen Händler regelt dieser die Ablöse direkt mit der
                      Bank.
                    </li>
                    <li>
                      <strong>Übergabe der Dokumente:</strong> Sobald die Bank
                      die Papiere freigibt, übergibst du Zulassungsschein,
                      Serviceheft, Pickerl und alle Schlüssel.
                    </li>
                    <li>
                      <strong>Abmeldung/Ummeldung:</strong> Melde das Auto ab
                      oder setze dem Käufer eine Frist zur Ummeldung, damit du
                      nicht weiter für Versicherung und Steuer haftest.
                    </li>
                  </ul>
                </>
              }
              rightSecondTitle="Preisfaktoren bei finanzierten Autos"
              rightSecondText={
                <>
                  <p>
                    Auch bei finanzierten Fahrzeugen bestimmen Alter,
                    Kilometerstand, Zustand, Marke, Modell und Ausstattung den
                    Wert. Ein gepflegtes Auto erzielt höhere Angebote. Achte
                    darauf, dass der Verkaufserlös die Restschuld deckt;
                    ansonsten musst du die Differenz begleichen.
                  </p>
                </>
              }
            />

            <HäufigeFragen
              title="Häufig gestellte Fragen"
              fragen={fragenAutoMitFinanzierungVerkaufen}
              image={FAQImage}
            />

            <div style={{ paddingTop: "120px" }}>
              <Ratgeber
                title="Mehr zum Thema Auto mit Finanzierung verkaufen"
                kaputtesAuto
                unfallAuto
                autoExport
                autoBewerten
                autoVerkaufen
                listenpreisBerechnen
              />
            </div>

            <div className="Banner-sections-marken">
              <BildTextInfo
                image={höchstenPreis}
                h2
                fontSize="36px"
                title="Fazit zum Thema Auto mit Finanzierung verkaufen"
                alt="Frau sitzt in einem Auto und nutzt ihr Smartphone, um mit Autoeinfachlos ein Auto trotz Finanzierung zu verkaufen."
                text={
                  <>
                    <p>
                      Der Verkauf eines finanzierten Autos erfordert
                      Vorbereitung, ist aber machbar. Kläre deine Restschuld,
                      vergleiche Optionen und profitiere von Autoeinfachlos, um
                      den Kredit sicher abzulösen und das Auto unkompliziert zu
                      verkaufen.
                    </p>
                  </>
                }
              />
            </div>
          </div>
          <Banner
            link="/auto-verkaufen"
            title="Jetzt Auto kostenlos bewerten & sicher verkaufen"
          />
        </div>
      )}
    </div>
  );
}

export default AutoMitFinanzierungVerkaufen;
